import { ChevronLeftIcon, ChevronRightIcon } from '@/components/base/Icons/RocheIconPack';
import { AdobeAnalyticsConst } from '@/hooks/AdobeAnalyticsHooks/adobeAnalyticsConstants/adobeAnalyticsConstants';
import { useCentralizedLinkClick } from '@/hooks/AdobeAnalyticsHooks/linkClick/useCentralizedLinkClick';
import IconButton from '@/storybook/components/IconButton/IconButton';
import cx from 'classnames';
import { FC, ReactNode } from 'react';
import styles from './ScrollableX.module.css';
import { useScrollable } from './useScrollable';

interface ScrollableProps {
  children: ReactNode;
  containerClassName?: string;
  scrollDistance?: number;
}

interface ScrollerProps {
  show: boolean;
  onClick: () => void;
}

const ScrollLeft = ({ show, onClick }: ScrollerProps) => {
  const { trackButtonClick } = useCentralizedLinkClick();

  const handleScrollLeftClick = () => {
    trackButtonClick({
      innertext: AdobeAnalyticsConst.BUTTON_TEXT.SCROLL_LEFT,
      position: AdobeAnalyticsConst.POSITIONS.SCROLLABLE_CONTAINER,
      name: AdobeAnalyticsConst.LINKCLICK_NAMES.SCROLLABLE_NAVIGATION,
    });
    onClick();
  };

  return (
    <>
      <div className={cx(styles.overlay, styles.left, { [styles.hidden]: !show })} />
      <IconButton
        appearance="fab"
        className={cx(styles.scrollableChevron, styles.left, { [styles.hidden]: !show })}
        icon={<ChevronLeftIcon />}
        onClick={handleScrollLeftClick}
      />
    </>
  );
};

const ScrollRight = ({ show, onClick }: ScrollerProps) => {
  const { trackButtonClick } = useCentralizedLinkClick();

  const handleScrollRightClick = () => {
    trackButtonClick({
      innertext: AdobeAnalyticsConst.BUTTON_TEXT.SCROLL_RIGHT,
      position: AdobeAnalyticsConst.POSITIONS.SCROLLABLE_CONTAINER,
      name: AdobeAnalyticsConst.LINKCLICK_NAMES.SCROLLABLE_NAVIGATION,
    });
    onClick();
  };

  return (
    <>
      <div className={cx(styles.overlay, styles.right, { [styles.hidden]: !show })} />
      <IconButton
        appearance="fab"
        className={cx(styles.scrollableChevron, styles.right, { [styles.hidden]: !show })}
        icon={<ChevronRightIcon />}
        onClick={handleScrollRightClick}
      />
    </>
  );
};

export const ScrollableX: FC<ScrollableProps> = ({ children, containerClassName = '', scrollDistance = 250 }) => {
  const { scrollContainerRef, isAtStart, isAtEnd, scrollToLeft, scrollToRight, handleDrag } = useScrollable({
    scrollDistance,
  });

  return (
    <div className={styles.scrollableWrapper}>
      <ScrollLeft show={!isAtStart} onClick={scrollToLeft} />
      <div
        ref={scrollContainerRef}
        role="region"
        className={cx(styles.scrollableContainer, containerClassName)}
        onMouseDown={handleDrag}
        onTouchStart={handleDrag}
      >
        {children}
      </div>
      <ScrollRight show={!isAtEnd} onClick={scrollToRight} aria-label="Scroll right" />
    </div>
  );
};
